import { motion, useReducedMotion } from 'framer-motion';
import type { CSSProperties } from 'react';

interface RotatingCubeProps {
  /** Up to 6 short labels, one per cube face. Defaults to TICCA operating stages. */
  faces?: string[];
  /** Face edge length in px. */
  size?: number;
  className?: string;
}

// A9 "Rotating_cubic_box" from animation.md — a preserve-3d cube that slowly
// tumbles. Recolored to the TICCA navy/cyan system (French border-image dropped),
// tumble slowed to ~18s, hover capped at a gentle scale, faces carry real labels
// (the 6 principles / 6 surfaces). Reduced-motion renders a static isometric cube.
const faceTransform = (i: number, tz: number): string =>
  [
    `translateZ(${tz}px)`,
    `rotateY(180deg) translateZ(${tz}px)`,
    `rotateY(90deg) translateZ(${tz}px)`,
    `rotateY(-90deg) translateZ(${tz}px)`,
    `rotateX(90deg) translateZ(${tz}px)`,
    `rotateX(-90deg) translateZ(${tz}px)`,
  ][i];

const DEFAULT_FACES = ['Intent', 'Evidence', 'Govern', 'Execute', 'Review', 'Improve'];

export function RotatingCube({ faces = DEFAULT_FACES, size = 150, className = '' }: RotatingCubeProps) {
  const reduce = useReducedMotion();
  const tz = size / 2;
  const labels = faces.slice(0, 6);

  const faceStyle: CSSProperties = {
    width: size,
    height: size,
    backfaceVisibility: 'hidden',
  };

  return (
    <div
      className={`mx-auto ${className}`}
      style={{ width: size, height: size, perspective: 900 }}
      aria-hidden
    >
      <motion.div
        className="relative h-full w-full"
        style={{ transformStyle: 'preserve-3d' }}
        initial={reduce ? { rotateX: -24, rotateY: -32 } : false}
        animate={reduce ? undefined : { rotateX: [0, 360], rotateY: [0, 360], rotateZ: [0, 360] }}
        transition={reduce ? undefined : { duration: 18, repeat: Infinity, ease: 'linear' }}
        whileHover={reduce ? undefined : { scale: 1.12 }}
      >
        {labels.map((label, i) => {
          const logoColors = ['#074EDC', '#FF8C00', '#4AAC00', '#ED0216', '#074EDC', '#FF8C00'];
          const c = logoColors[i % logoColors.length];
          return (
            <div
              key={label + i}
              className="absolute flex items-center justify-center rounded-md border bg-slate-900/90 p-3 text-center text-white/80 shadow-[inset_0_0_28px_-6px_rgba(7,78,220,0.4)]"
              style={{ ...faceStyle, transform: faceTransform(i, tz), borderColor: `${c}80`, color: c }}
            >
              {label}
            </div>
          );
        })}
      </motion.div>
    </div>
  );
}
